import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Code,
    title: "Custom Website Development",
    description:
      "Fully tailored websites aligned with your brand, audience, and growth goals.",
  },
  {
    icon: Smartphone,
    title: "Responsive Website Design",
    description:
      "Mobile-first, cross-device compatibility for flawless performance everywhere.",
  },
  {
    icon: Zap,
    title: "Performance Optimization",
    description:
      "Fast-loading architecture optimized for Core Web Vitals and user experience.",
  },
  {
    icon: Shield,
    title: "Security-First Development",
    description:
      "SSL, structured backend logic, secure coding standards, and safe hosting practices.",
  },
  {
    icon: TrendingUp,
    title: "SEO Integration",
    description:
      "On-page SEO foundations built during development — not added later.",
  },
  {
    icon: Layers,
    title: "CMS Integration",
    description:
      "WordPress, headless CMS, or custom admin dashboards depending on your needs.",
  },
];

const offerings = [
  {
    title: "Corporate Websites",
    description:
      "Credibility-driven websites for professional brands and service businesses.",
    features: [
      "Brand-aligned UI",
      "Clear messaging",
      "Service page structuring",
      "Contact funnel optimization",
    ],
  },
  {
    title: "High-Converting Landing Pages",
    description: "Built for paid ads, SEO campaigns, or product launches.",
    features: [
      "A/B testing ready",
      "Form optimization",
      "CTA placement strategy",
      "Tracking & analytics setup",
    ],
  },
  {
    title: "Web Applications",
    description:
      "Custom-built platforms with secure login systems and advanced functionality.",
    features: [
      "Database architecture",
      "API integrations",
      "Real-time features",
      "Scalable backend logic",
    ],
  },
  {
    title: "Portal Development",
    description: "Client or internal dashboards with role-based access.",
    features: [
      "Document systems",
      "Analytics dashboards",
      "CRM integrations",
      "Automation workflows",
    ],
  },
];

const deliverables = [
  "Fully responsive custom design",
  "SEO-optimized page structure",
  "Clean, scalable codebase",
  "Core Web Vitals optimization",
  "SSL & security implementation",
  "CMS access & training",
  "Analytics & tracking setup",
  "30-day post-launch support",
];

const processSteps = [
  {
    step: "01",
    title: "Discovery",
    description:
      "We analyze your business, competitors, and target audience to define structure and objectives.",
  },
  {
    step: "02",
    title: "Strategic Planning",
    description:
      "Sitemap creation, wireframes, keyword mapping, and performance planning.",
  },
  {
    step: "03",
    title: "Design",
    description: "User-focused UI/UX built to guide visitors toward action.",
  },
  {
    step: "04",
    title: "Development",
    description: "Clean, optimized coding with technical SEO built in.",
  },
  {
    step: "05",
    title: "Testing",
    description:
      "Cross-device testing, speed optimization, and performance validation.",
  },
  {
    step: "06",
    title: "Launch & Support",
    description:
      "Smooth deployment with monitoring and optional ongoing optimization.",
  },
];

const faqs = [
  {
    question: "How long does it take to build a website?",
    answer:
      "Most small business website development projects in Vancouver take 3–6 weeks depending on complexity, integrations, and content readiness. Larger web applications may require additional planning phases.",
  },
  {
    question: "What platforms do you develop on?",
    answer:
      "We work with WordPress, custom React/Node stacks, headless CMS setups, and tailored admin systems — depending on scalability and performance requirements.",
  },
  {
    question: "Will my website be SEO-friendly?",
    answer:
      "Yes. SEO structure is implemented during development. We build proper URL hierarchy, internal linking structure, optimized images, and metadata readiness from day one.",
  },
  {
    question: "Can I update the website myself?",
    answer:
      "Yes. We provide CMS access and training so your team can update content without technical knowledge.",
  },
  {
    question: "What happens after launch?",
    answer:
      "We monitor performance, provide support, and can continue with SEO services to grow traffic and leads.",
  },
];

const whyChooseUs = [
  {
    title: "100% Project Commitment",
    description:
      "Every project is handled with structured planning, defined milestones, and transparent communication.",
  },
  {
    title: "SEO-Ready Architecture",
    description:
      "Your website is built with proper structure, schema readiness, optimized page hierarchy, and crawl-friendly development.",
  },
  {
    title: "Conversion-Focused Design",
    description:
      "We apply proven UX patterns, strategic CTAs, and layout psychology to increase inquiries and leads.",
  },
  {
    title: "Dedicated Development Team",
    description:
      "You work directly with developers and strategists — no outsourced chaos.",
  },
];

export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      {/*  SEO Meta */}
      <SEO
        title="Website Development Agency in Vancouver | Custom Web Design"
        description="Professional website development agency in Vancouver. We build custom, responsive, SEO-friendly websites designed to grow your business across Canada."
        ogTitle="Website Development Agency in Vancouver | Custom Web Design"
        ogDescription="Professional website development agency in Vancouver. We build custom, responsive, SEO-friendly websites designed to grow your business across Canada."
      />

      {/* Hero Section */}
<section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
  <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
  <div className="absolute inset-0">
    <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
    <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <div className="max-w-4xl text-center sm:text-left">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex justify-center sm:justify-start"
      >
        <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
          <Globe className="h-4 w-4 mr-2" />
          Our Services
        </Badge>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
      >
        Website Development Agency in{" "}
        <span className="gradient-text">Vancouver</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-lg sm:text-xl text-background/70 mb-4 sm:mb-5 max-w-2xl leading-relaxed mx-auto sm:mx-0"
      >
        Custom, high-performance websites built for small and mid-sized Canadian
        businesses that want measurable growth — not just a good-looking site.
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="text-base sm:text-lg text-background/65 mb-8 max-w-2xl leading-relaxed mx-auto sm:mx-0"
      >
        We design SEO-friendly, conversion-focused websites that position your brand
        as an authority and turn visitors into qualified leads.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center sm:items-start"
      >
        <Button
          variant="gradient"
          size="lg"
          asChild
          className="group w-full sm:w-auto justify-center"
        >
          <Link to="/get-started" className="flex items-center justify-center gap-2">
            Start Your Project
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>

        <Button
          variant="glass"
          size="lg"
          asChild
          className="text-background border-background/30 hover:bg-background/10 w-full sm:w-auto justify-center"
        >
          <Link to="/portfolio/case-studies">View Our Work</Link>
        </Button>
      </motion.div>
    </div>
  </div>
</section>

      {/* Value Statement */}
      <section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              Your Website Is Your{" "}
              <span className="gradient-text">Digital Foundation</span>
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-8">
              For most businesses, your website is the first interaction
              customers have with your brand. If it loads slowly, looks outdated,
              or lacks structure, you lose trust instantly.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/60 backdrop-blur rounded-2xl border border-border shadow-soft p-6 sm:p-8"
            >
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
                As a professional web development company in Vancouver, we build
                custom website development solutions designed around:
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                {[
                  {
                    title: "Clear messaging",
                    desc: "Instant clarity on what you offer.",
                  },
                  { title: "Strategic layout", desc: "Structure that guides action." },
                  { title: "Technical SEO", desc: "Built to index and scale." },
                  {
                    title: "Conversion focus",
                    desc: "Design backed by psychology.",
                  },
                  { title: "Scalable build", desc: "Future-ready architecture." },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-left hover:shadow-medium transition-shadow"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <p className="text-lg sm:text-xl font-display font-semibold text-foreground">
                  We don’t just design websites.
                </p>
                <p className="mt-2 text-base sm:text-lg text-muted-foreground leading-relaxed">
                  We build digital assets that support long-term business growth.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              Why Choose <span className="gradient-text">Square Trigger</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
              We are a strategy-first website development agency focused on
              performance, clarity, and measurable outcomes.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow text-left"
              >
                <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-14"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              What We <span className="gradient-text">Deliver</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover">
                    <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                      {capability.title}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {capability.description}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-secondary uppercase tracking-wider">
              Solutions
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold mt-3">
              What We <span className="gradient-text">Offer</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="bg-background/5 rounded-2xl p-6 sm:p-8 border border-background/10 hover:border-primary/30 transition-colors"
              >
                <h3 className="text-xl sm:text-2xl font-display font-bold mb-3">
                  {offering.title}
                </h3>

                <p className="text-background/70 mb-6 leading-relaxed">
                  {offering.description}
                </p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {offering.features.map((feature, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm sm:text-base text-background/80"
                    >
                      <CheckCircle className="h-5 w-5 text-secondary mt-[2px] flex-shrink-0" />
                      <span className="leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                Deliverables
              </span>

              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3 mb-5">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Every website development project includes:
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/get-started"
                  className="flex items-center justify-center gap-2"
                >
                  Get Started
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-card rounded-2xl p-6 sm:p-8 border border-border shadow-soft"
            >
              <ul className="space-y-4">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-[2px]" />
                    <span className="text-sm sm:text-base text-foreground/90 leading-relaxed">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-7 pt-6 border-t border-border">
                <p className="text-sm sm:text-base font-semibold text-foreground">
                  No hidden surprises.
                </p>
                <p className="text-sm sm:text-base font-semibold text-foreground">
                  No shortcuts.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="pt-12 sm:pt-14 lg:pt-12 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              How We <span className="gradient-text">Build Your Website</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="p-6 h-full border-border hover:border-primary/30 transition-colors card-hover">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-3xl font-display font-bold gradient-text">
                      {step.step}
                    </div>
                    <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                    </div>
                  </div>

                  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies CTA */}
      <section className="py-16 sm:py-20 gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4 sm:mb-6">
              See Our Website Development in Action
            </h2>

            <p className="text-base sm:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Explore real projects and see how we&apos;ve helped Canadian
              businesses build scalable digital platforms.
            </p>

            <Button
              variant="premium"
              size="lg"
              asChild
              className="bg-background text-foreground hover:bg-background/90 w-full sm:w-auto justify-center"
            >
              <Link
                to="/portfolio/case-studies"
                className="flex items-center justify-center gap-2"
              >
                View Case Studies
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              Website Development –{" "}
              <span className="gradient-text">Frequently Asked Questions</span>
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
           {faqs.map((faq, index) => (
  <motion.div
    key={faq.question}
    className="relative z-10 bg-card rounded-xl border border-border overflow-hidden"
  >
    <button
      type="button"
      onPointerUp={() => setOpenFaq(openFaq === index ? null : index)}
      className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors pointer-events-auto touch-manipulation"
    >
      <span className="font-semibold text-foreground pr-6">
        {faq.question}
      </span>
      <ChevronDown
        className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
          openFaq === index ? "rotate-180" : ""
        }`}
      />
    </button>

    {/* Answer (mobile-safe) */}
<div
  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
    openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
  }`}
>
  <div className="overflow-hidden">
    <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
      {faq.answer}
    </div>
  </div>
</div>

  </motion.div>
))}

          </div>

          {/* FAQ Schema */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answer,
                },
              })),
            })}
          </script>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
              Ready to Build Your <span className="gradient-text">Dream Website</span>?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
              Let’s create a website that strengthens your brand, improves
              visibility, and drives measurable business results.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/get-started"
                  className="flex items-center justify-center gap-2"
                >
                  Start Your Project
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="outline"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center"
              >
                <Link to="/contact" className="flex items-center justify-center">
                  Contact Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}